import { Menu } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { getMenuItemByRoute, ROUTES } from '@/constants';
import { cn } from '@/utils/cn';

import { Button } from './Button';

interface HeaderProps {
  onMenuClick?: () => void;
  onShuttleModeClick?: () => void;
  onBreakModeClick?: () => void;
  className?: string;
  sidebarOpen?: boolean;
}

// Page title mapping for routes not in menu config
const SPECIAL_ROUTE_TITLES: Record<string, { key: string; fallback: string }> = {
  [ROUTES.plantSelection]: { key: 'navigation.plantSelection', fallback: 'Plant Selection' },
  [ROUTES.changePlant]: { key: 'navigation.plantSelection', fallback: 'Plant Selection' },
  [ROUTES.maintenance]: { key: 'navigation.maintenance', fallback: 'Maintenance' },
  [ROUTES.error]: { key: 'navigation.error', fallback: 'Error' },
  [ROUTES.logoutSummary]: { key: 'navigation.logoutSummary', fallback: 'Logout Summary' },
};

export function Header({
  onMenuClick,
  onShuttleModeClick,
  onBreakModeClick,
  className,
  sidebarOpen = false,
}: HeaderProps) {
  const { t } = useTranslation();
  const location = useLocation();

  // Track online/offline status — matches Xamarin connected icon
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };
    const handleOffline = () => {
      setIsOnline(false);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Get dynamic page title based on current route
  const getPageTitle = (): string => {
    // Check menu items first
    const menuItem = getMenuItemByRoute(location.pathname);
    if (menuItem) {
      return t(menuItem.labelKey, menuItem.label);
    }

    // Handle special routes not in menu
    const specialRoute = SPECIAL_ROUTE_TITLES[location.pathname];
    if (specialRoute) {
      return t(specialRoute.key, specialRoute.fallback);
    }

    // Default to Dashboard
    return t('navigation.dashboard', 'Dashboard');
  };

  const pageTitle = getPageTitle();

  // Hide hamburger menu and page title on work mode selection page
  const isWorkModeSelection = location.pathname === ROUTES.workModeSelection;

  return (
    <header className={cn('sticky top-0 z-30 w-full bg-white', className)}>
      <div className="flex h-14 items-center px-4 w-full">
        {/* Menu Button - Hidden on work mode selection page */}
        {!isWorkModeSelection && (
          <Button
            variant="ghost"
            size="icon"
            className={cn('mr-2', sidebarOpen && 'md:invisible')}
            onClick={onMenuClick}
            aria-label={t('sidebar.toggleMenu')}
          >
            <Menu className="h-5 w-5 text-gray-800" />
          </Button>
        )}

        {/* Dynamic Page Title - Hidden on work mode selection page */}
        {!isWorkModeSelection && (
          <h1 className="text-base sm:text-xl font-bold font-poppins text-[#1A1A1A] truncate min-w-0 flex-1">
            {pageTitle}
          </h1>
        )}

        {/* Spacer - only needed when title is hidden */}
        {isWorkModeSelection && <div className="flex-1" />}

        {/* Toolbar Icons */}
        <div className="flex items-center gap-4 shrink-0">
          {/* Shuttle Mode Button */}
          <button
            onClick={onShuttleModeClick}
            className="shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('header.shuttleMode', 'Shuttle Mode')}
          >
            <img
              src="/images/header/shuttle_icon.png"
              alt=""
              className="h-6 w-6 shrink-0"
              aria-hidden="true"
            />
          </button>

          {/* Break Mode Button */}
          <button
            onClick={onBreakModeClick}
            className="shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('header.breakMode', 'Break Mode')}
          >
            <img
              src="/images/header/break_icon.png"
              alt=""
              className="h-6 w-6 shrink-0"
              aria-hidden="true"
            />
          </button>

          {/* Connected Status Icon — Matches Xamarin online/offline indicator */}
          <div
            className="shrink-0 p-2"
            aria-label={isOnline ? t('header.online', 'Online') : t('header.offline', 'Offline')}
            role="status"
          >
            <img
              src={isOnline ? '/images/header/online.png' : '/images/header/offline.png'}
              alt={isOnline ? t('status.connected') : t('status.disconnected')}
              className="h-6 w-6 shrink-0"
            />
          </div>
        </div>
      </div>
    </header>
  );
}

Header.displayName = 'Header';
